import type { NodeType, ProjectDocument, ProjectNode, ProjectSettings } from '../types/project';
import {
  resolveNodeAppearance,
  resolveNodeTypeAppearance,
} from './nodeVisualGeometry';

// ------------------------------------------------------------
// Node palette: labels, colors, defaults (brand-aligned)
// ------------------------------------------------------------

export const NODE_TYPE_LABELS: Record<NodeType, string> = {
  input: 'Source',
  output: 'Sink',
  splitter: 'Splitter',
  merger: 'Merger',
  diverger: 'Diverger',
  processor: 'Processor',
  gate: 'Gate',
  waypoint: 'Waypoint',
};

export const NODE_TYPE_DESCRIPTIONS: Record<NodeType, string> = {
  input: 'Material or flow entry point',
  output: 'Material or flow exit point',
  splitter: 'Split one stream into several',
  merger: 'Combine streams into one',
  diverger: 'Route items by rule',
  processor: 'Transform or process items',
  gate: 'Conditional flow control',
  waypoint: 'Route bend / anchor point',
};

/** Distinct type colors — tuned for dark UI legibility.
 *  NODE_COLORS and nodeDisplayColor are for editor nodes only.
 *  Product rendering must use infeed product type definitions, never this palette.
 */
export const NODE_COLORS: Record<NodeType, string> = {
  input: '#34D399',      // emerald — Source
  output: '#F05252',     // coral red — Sink
  splitter: '#38BDF8',   // sky blue
  merger: '#A78BFA',     // violet
  diverger: '#FB923C',   // orange
  processor: '#E879F9',  // fuchsia
  gate: '#60A5FA',       // blue
  waypoint: '#9CA3AF',   // cool grey
};

export const NODE_TOOLS = (
  Object.keys(NODE_TYPE_LABELS) as NodeType[]
).map((type) => ({
  type,
  label: NODE_TYPE_LABELS[type],
  description: NODE_TYPE_DESCRIPTIONS[type],
  color: NODE_COLORS[type],
}));

export function nodeTypeColor(type: string, settings?: ProjectSettings): string {
  return resolveNodeTypeAppearance(type as NodeType, settings).color;
}

/** Canonical display color from node type (project overrides, then NODE_COLORS). */
export function nodeDisplayColor(
  node: ProjectNode,
  settings?: ProjectSettings,
): string {
  return resolveNodeAppearance(node, settings).color;
}

export function nodeDisplayRadius(
  node: ProjectNode,
  settings?: ProjectSettings,
): number {
  return resolveNodeAppearance(node, settings).radius;
}

export function nodeDisplayShape(
  node: ProjectNode,
  settings?: ProjectSettings,
) {
  return resolveNodeAppearance(node, settings).shape;
}

export function withTypeNodeColors(document: ProjectDocument): ProjectDocument {
  const settings = document.settings;
  return {
    ...document,
    nodes: document.nodes.map((node) => ({
      ...node,
      color: nodeDisplayColor(node, settings),
      radius: nodeDisplayRadius(node, settings),
    })),
  };
}

export function resolveNodeType(node: ProjectNode): string {
  const raw = node.type ?? node.node_type;
  return typeof raw === 'string' ? raw : '';
}

export function defaultNodeName(type: NodeType, existingNames: string[]): string {
  const base = NODE_TYPE_LABELS[type];
  let n = 1;
  let name = `${base} ${n}`;
  while (existingNames.includes(name)) {
    n += 1;
    name = `${base} ${n}`;
  }
  return name;
}

export function snapValue(value: number, gridSize: number, enabled: boolean): number {
  if (!enabled || gridSize <= 0) return Math.round(value);
  return Math.round(value / gridSize) * gridSize;
}
